<template>
  <div class="d-flex flex-column justify-content-end w-100">
    <div class="d-flex justify-content-between align-items-center mb-2 px-2 mx-1">
      <span class="fw-bold">{{ $t("admin.studeaManagerProfile.all_access") }}</span>
      <SwitchTypeComponent :model="switchAllModel" @update="switchAll" />
    </div>
    <div class="role">
      <div class="role__item" v-for="(domain, label) in permissions" :key="label">
        <div class="role__label">
          {{ label }}
          <button
            v-tooltip="{ title: $t('admin.studeaManagerProfile.block_switch_help'), textWrap: true }"
            type="button"
            class="role__help"
            tabindex="0"
          >
            <FontAwesomeIcon icon="fa-light fa-circle-question" />
          </button>
          <SwitchTypeComponent @draft="updateSwitch" :model="switchModels[label]" />
        </div>
        <div
          class="role__permissions"
          :class="{ '-off': !switchModels[label].vars.value }"
          :inert="!switchModels[label].vars.value"
        >
          <div class="role__permission" v-for="permission in domain" :key="permission.access.id">
            <div class="role__name">
              {{ permission.access.name }}
            </div>
            <div class="role__select">
              <SelectTypeComponent
                :model="permissionModels[permission.access.name]"
                :ignoreBwIgnore="true"
                @draft="updated"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SelectTypeComponent from "@/components/form/SelectTypeComponent.vue";
import SwitchTypeComponent from "@/components/form/SwitchTypeComponent.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

export default {
  name: "RoleTypeComponent",
  components: { SwitchTypeComponent, SelectTypeComponent, FontAwesomeIcon },
  emits: ["draft", "updated"],
  props: {
    model: {
      type: Object,
      default: () => ({ vars: { name: "", value: [] } }),
    },
  },
  data() {
    return {
      itemModel: this.model,
      permissions: {},
      permissionModels: {},
      switchModels: {},
      switchAllModel: {
        vars: {
          value: true,
          name: "switchAll",
          id: "switchAll",
          block_prefixes: ["", "switch"],
        },
      },
    };
  },
  mounted() {
    this.processPermissions();
    this.setSwitchAllModel();
  },
  methods: {
    updated(model) {
      const item = this.itemModel.vars.value.find((item) => item.access.name === model.vars.name);
      item.level = model.vars.value.data;
      this.permissionModels[item.access.name] = this.buildPermissionModel(item);
      this.$emit("updated", this.itemModel);
      this.$emit("draft", this.itemModel);
    },

    setSwitchModel(label, domain) {
      let open = false;
      if (Object.keys(domain).length) {
        Object.values(domain).forEach((permission) => {
          if (!open && permission.level) {
            open = true;
          }
        });
      }
      return {
        vars: {
          value: open,
          name: label,
          id: label,
          block_prefixes: ["", "switch"],
        },
      };
    },

    setSwitchAllModel() {
      let open = true;
      if (Object.keys(this.permissions).length && open) {
        Object.values(this.permissions).forEach((domain) => {
          if (Object.keys(domain).length && open) {
            Object.values(domain).forEach((permission) => {
              const maxLevel = permission.access.levelsAllowed.length === 4 ? 3 : 4;
              if (open && permission.level < maxLevel) {
                open = false;
              }
            });
          }
        });
      }
      this.switchAllModel.vars.value = open;
    },

    switchAll(value) {
      Object.values(this.switchModels).forEach((model) => {
        model.vars.value = value;
        this.updateSwitch(model);
      });
    },

    // Ne mute plus les Access du domaine (RG1.8.4) : l'interrupteur de bloc ne fait
    // que refléter/piloter un état visuel (grisé + non interactif), les valeurs
    // enregistrées des droits fins ne sont jamais écrasées ni remises à zéro ici.
    updateSwitch(model) {
      this.switchAllModel.vars.value = !model.vars.value ? false : this.switchAllModel.vars.value;
    },

    buildPermissionModel(permission) {
      let choices = [];
      permission.access.levelsAllowed.forEach((level) => {
        choices.push({
          label: this.$t("admin.user.role." + level),
          value: this.value(level).toString(),
          data: this.value(level),
        });
      });
      const value = choices.find((choice) => choice.data === permission.level);

      return {
        vars: {
          value: value,
          block_prefixes: ["", "choice"],
          expanded: false,
          required: true,
          multiple: false,
          choices: choices,
          id: permission.access.name,
          name: permission.access.name,
          attr: {},
        },
      };
    },

    value(level) {
      let value = 0;
      switch (level) {
        case "VIEW":
          value = 1;
          break;
        case "EDIT":
          value = 2;
          break;
        case "DELETE":
          value = 3;
          break;
        case "ALL":
          value = 4;
          break;
      }
      return value;
    },

    processPermissions() {
      Object.values(this.itemModel.vars.value).forEach((permission) => {
        if (!this.permissions[permission.access.domain.name]) {
          this.permissions[permission.access.domain.name] = [];
        }
        this.permissions[permission.access.domain.name].push(permission);
        this.switchModels[permission.access.domain.name] = this.setSwitchModel(
          permission.access.domain.name,
          this.permissions[permission.access.domain.name],
        );
        this.permissionModels[permission.access.name] = this.buildPermissionModel(permission);
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.role {
  border: 1px dashed $m-color_4;
  border-radius: 6px;

  &__label {
    font-size: 18px;
    font-weight: 700;
    padding: 5px 10px;
    border-bottom: 1px dashed $m-color_4;
    display: flex;
    justify-content: space-between;
  }

  &__help {
    display: inline-flex;
    align-items: center;
    border: none;
    background: transparent;
    padding: 0;
    margin: 0 8px;
    color: inherit;
    cursor: pointer;
  }

  &__permissions {
    opacity: 1;
    pointer-events: auto;

    @media (prefers-reduced-motion: no-preference) {
      transition: opacity 200ms ease-in-out;
    }

    &.-off {
      opacity: 0.35;
      pointer-events: none;
    }
  }

  &__permission {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px dashed $m-color_4;
    padding: 5px 10px;
    align-items: center;
  }

  &__select {
    max-width: 250px;
    flex: 1;
  }
}
</style>
